import { readPublicConfig } from "../config";
import { readAdminSession } from "./adminRepository";

export type CreatePersonalProposalInput = {
  recipientName: string;
  recipientEmail?: string;
  message: string;
  offerId: string;
  variantId: string;
  locationId: string;
  displayPriceCents?: number;
  currency?: string;
  imageUrl?: string;
  expiresAt?: string;
  options: Array<{
    startsAt: string;
    timezone: string;
  }>;
};

export type CreatedPersonalProposal = {
  source: "live";
  proposalId: string;
  token: string;
  link: string;
  recipientName: string;
  offerTitle: string;
  variantLabel: string;
  locationName: string;
  priceCents: number | null;
  currency: string;
  options: Array<{ id: string; startsAt: string; endsAt: string }>;
};

export async function createPersonalProposal(
  input: CreatePersonalProposalInput,
): Promise<CreatedPersonalProposal> {
  const session = await readAdminSession();
  if (!session) throw new Error("Die Anmeldung ist abgelaufen. Bitte erneut anmelden.");

  const config = readPublicConfig(import.meta.env);
  const endpoint = `${config.bookingApiUrl.replace(/\/booking-api$/, "")}/appointment-proposals`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      action: "create_proposal",
      ...input,
    }),
  });

  const payload = (await response.json().catch(() => ({}))) as CreatedPersonalProposal & {
    error?: string;
  };
  if (!response.ok) throw new Error(proposalError(payload.error));
  return payload;
}

function proposalError(error?: string): string {
  switch (error) {
    case "authentication_required":
      return "Die Anmeldung ist abgelaufen. Bitte erneut anmelden.";
    case "admin_access_denied":
      return "Dieser Zugang darf keine persönlichen Vorschläge erstellen.";
    case "invalid_recipient":
      return "Bitte den Namen der eingeladenen Person eintragen.";
    case "invalid_recipient_email":
      return "Bitte die E-Mail-Adresse prüfen.";
    case "offer_not_found":
      return "Das ausgewählte Angebot ist nicht mehr aktiv.";
    case "variant_not_found":
      return "Die ausgewählte Dauer ist nicht mehr aktiv.";
    case "location_not_found":
    case "location_not_available_for_offer":
      return "Der ausgewählte Ort ist für dieses Angebot nicht verfügbar.";
    case "invalid_display_price":
      return "Bitte einen gültigen persönlichen Preis eintragen.";
    case "invalid_image_url":
      return "Das Bild muss über eine sichere HTTPS-Adresse erreichbar sein.";
    case "invalid_expiry":
      return "Die Einladung muss in der Zukunft ablaufen.";
    case "invalid_proposal_options":
      return "Bitte ein bis sechs konkrete Zeiten auswählen.";
    case "invalid_proposal_option":
      return "Mindestens eine Zeit liegt in der Vergangenheit oder ist unvollständig.";
    case "duplicate_proposal_option":
      return "Eine vorgeschlagene Zeit wurde doppelt eingetragen.";
    case "proposal_duration_mismatch":
      return "Die Dauer der Zeit passt nicht zum ausgewählten Angebot.";
    default:
      return "Der persönliche Vorschlag konnte gerade nicht erstellt werden.";
  }
}
